import { Button, ButtonProps, ButtonVariant, ComponentTypes } from "@mcleod/components";
import { StringUtil } from "@mcleod/core";
import { MobileDesignTokens } from "../tokens/MobileDesignTokens";

/**
 * The button every `"type": "button"` in a .layout becomes when the layout is deserialized inside the mobile app.
 *
 * It applies the mobile design tokens as *defaults only*: anything the layout (or its `appProps`) states explicitly
 * is left alone, so dense screens can still opt out per component.  Buttons constructed from TypeScript are not
 * affected; use `AppButton.applyMobileDefaults()` or the tokens directly there.
 *
 * Rules applied (Material 3 / Apple HIG):
 *   - text buttons: at least 44px tall, 16px label, 12px corner radius, 20px horizontal padding;
 *   - icon-only buttons: at least a 44x44 hit area with a 24px glyph.
 *
 * Timing matters: the deserializer constructs a component with only its id and applies the layout's props
 * afterwards, so the constructor cannot tell a round icon button from a text button.  Deserialized buttons
 * therefore wait for `doAfterDeserialize()` and read their own, now complete, props; everything else (code that
 * passes full props to the constructor) is handled immediately.
 */
export class AppButton extends Button {
    protected constructor(props?: Partial<ButtonProps>) {
        super(props);
        if (this._designer == null && !this.isDeserializing())
            AppButton.applyMobileDefaults(this, props);
    }

    override doAfterDeserialize(): void {
        super.doAfterDeserialize();
        if (this._designer == null)
            AppButton.applyMobileDefaults(this);
    }

    /**
     * Applies the token defaults to `button` for every sizing prop that was not set explicitly.  Pass `props` when
     * they are the complete set the button was constructed with; omit them to judge by the button's current values
     * (the right choice once a layout has finished applying its own).
     */
    public static applyMobileDefaults(button: Button, props?: Partial<ButtonProps>): void {
        const explicit = <K extends keyof ButtonProps>(key: K): Partial<ButtonProps>[K] =>
            props != null ? props[key] : (button as unknown as Partial<ButtonProps>)[key];
        const isSet = (key: keyof ButtonProps): boolean => explicit(key) != null;

        const tokens = MobileDesignTokens.button;
        const isIconOnly = explicit("variant") === ButtonVariant.round
            || (StringUtil.isEmptyString(explicit("caption")) && !StringUtil.isEmptyString(explicit("imageName")));

        if (isIconOnly) {
            if (!isSet("width") && !isSet("minWidth"))
                button.minWidth = MobileDesignTokens.touchTarget.min;
            if (!isSet("height") && !isSet("minHeight"))
                button.minHeight = MobileDesignTokens.touchTarget.min;
            if (!isSet("imageWidth") && !isSet("imageHeight")) {
                button.imageWidth = tokens.iconSize;
                button.imageHeight = tokens.iconSize;
            }
            return;
        }

        if (!isSet("height") && !isSet("minHeight"))
            button.minHeight = tokens.minHeight;
        if (!isSet("fontSize"))
            button.fontSize = tokens.fontSize;
        if (!isSet("borderRadius") && explicit("variant") !== ButtonVariant.text)
            button.borderRadius = tokens.radius;
        if (!isSet("padding") && !isSet("paddingLeft") && !isSet("paddingRight")) {
            button.paddingLeft = tokens.paddingHorizontal;
            button.paddingRight = tokens.paddingHorizontal;
        }
    }
}

ComponentTypes.registerAppComponentType("button", {
    constructor: AppButton.prototype.constructor
});
